import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Scales, MagnifyingGlass, Files, ChatCircleDots, Books, Buildings, House, CalendarBlank, UsersThree, Gavel, SignOut, UserCircle, Shield } from "@phosphor-icons/react";
import LexiqLogo from "./LexiqLogo";
import LexiqWordmark from "./LexiqWordmark";
import BottomNav from "./BottomNav";
import MobileTopBar from "./MobileTopBar";
import { useUser } from "../context/UserAuthContext";
import { useAdmin } from "../context/AdminAuthContext";
import NotificationBell from "./notifications/NotificationBell";

const NAV_LINKS = [
  { to: "/", label: "الرئيسية", testid: "nav-home", icon: House },
  { to: "/analyze", label: "تحليل قضية", testid: "nav-analyze", icon: MagnifyingGlass },
  { to: "/draft", label: "صياغة وثيقة", testid: "nav-draft", icon: Files },
  { to: "/chat", label: "الأسئلة القانونية", testid: "nav-chat", icon: ChatCircleDots },
  { to: "/cases", label: "قضاياي", testid: "nav-cases", icon: Scales },
  { to: "/timeline", label: "الجدول الزمني", testid: "nav-timeline", icon: CalendarBlank },
  { to: "/clients", label: "العملاء", testid: "nav-clients", icon: UsersThree },
  { to: "/cassation", label: "قرارات التمييز", testid: "nav-cassation", icon: Gavel },
  { to: "/laws", label: "القوانين", testid: "nav-laws", icon: Books },
  { to: "/courts", label: "المحاكم", testid: "nav-courts", icon: Buildings },
];

export default function Layout({ children }) {
  const location = useLocation();
  const nav = useNavigate();
  const { user, logout } = useUser();
  const { admin } = useAdmin();

  const onLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    // iOS WKWebView ONLY gives you butter-smooth momentum scroll + the
    // rubber-band bounce when the scroll container has an EXPLICIT
    // bounded height + `overflow-y: auto`. Letting the body scroll
    // freely inside a `min-h-screen flex-col` container makes the
    // bounce disappear and scrolling feels stiff (the user complained
    // that only the case-detail Dialog scrolls smoothly — that Dialog
    // already follows this pattern, every other page didn't).
    //
    // `h-[100dvh]` pins the shell to the visible viewport, `flex-col`
    // stacks header/main/footer, and `<main>` gets `overflow-y-auto`
    // so iOS treats IT as the scroll view (momentum + bounce). Inline
    // `WebkitOverflowScrolling: touch` is the magic flag that re-
    // enables momentum on older WebKit builds.
    //
    // The inline `height` formula uses the native keyboard listener
    // (configureNativeChrome → Keyboard.addListener) that exposes the
    // live keyboard height as `--keyboard-h`. Shrinking the shell in
    // lockstep with the keyboard animation gives the WhatsApp-style
    // glide: the chat input slides up perfectly synced with iOS's own
    // keyboard easing instead of jumping after the keyboard has
    // already finished animating.
    <div className="h-[100dvh] overflow-hidden flex flex-col bg-white" dir="rtl">
      <MobileTopBar />
      <header className="hidden lg:block sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#EAEAEA] shrink-0">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-5 flex items-center justify-between gap-3 sm:gap-6">
          <NavLink to="/" data-testid="brand-logo" className="flex items-center gap-2 sm:gap-3 group min-w-0">
            <div className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center shrink-0">
              <LexiqLogo size={44} />
            </div>
            <div className="leading-tight min-w-0">
              <div className="text-lg sm:text-xl"><LexiqWordmark className="text-lg sm:text-xl" testid="brand-lexiq" /></div>
              <div className="hidden sm:block text-[10px] tracking-[0.25em] text-[#8A8A8A] uppercase">Justice + AI</div>
            </div>
          </NavLink>

          <nav className="hidden lg:flex items-center gap-7" aria-label="primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `editorial-link text-sm font-cairo ${isActive ? "active" : ""}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <NavLink to="/analyze" data-testid="header-cta" className="hidden lg:inline-flex lex-btn-gold text-xs sm:text-sm whitespace-nowrap px-3 sm:px-6 py-2 sm:py-3">
            ابدأ تحليل قضية
          </NavLink>

          {/* User menu */}
          <div className="hidden lg:flex items-center gap-2">
            {admin && (
              <NavLink
                to="/admin"
                data-testid="header-admin-link"
                className="flex items-center gap-1 px-3 py-2 border border-[#D4AF37] text-[#D4AF37] text-xs hover:bg-[#D4AF37] hover:text-white transition"
                title="لوحة الإدارة"
              >
                <Shield size={14} weight="duotone" />
                <span>لوحة الإدارة</span>
              </NavLink>
            )}
            {user ? (
              <>
                <NotificationBell />
                <span className="text-sm text-[#0A192F] flex items-center gap-1.5" data-testid="user-display-name">
                  <UserCircle size={18} weight="duotone" className="text-[#D4AF37]" />
                  {user.name}
                </span>
                <button
                  onClick={onLogout}
                  data-testid="user-logout-btn"
                  className="flex items-center gap-1 px-3 py-2 border border-[#EAEAEA] text-[#0A192F] text-xs hover:bg-[#0A192F] hover:text-white transition"
                  title="تسجيل خروج"
                >
                  <SignOut size={14} />
                  <span>خروج</span>
                </button>
              </>
            ) : (
              <>
                <NavLink to="/login" data-testid="header-login" className="px-3 py-2 border border-[#0A192F] text-[#0A192F] text-xs hover:bg-[#0A192F] hover:text-white transition">
                  دخول
                </NavLink>
                <NavLink to="/signup" data-testid="header-signup" className="px-3 py-2 bg-[#0A192F] text-white text-xs hover:bg-[#D4AF37] hover:text-[#0A192F] transition">
                  تسجيل
                </NavLink>
              </>
            )}
          </div>
        </div>

        {/* Mobile nav — replaced by BottomNav (kept space below) */}
      </header>

      <main
        key={location.pathname}
        className="flex-1 lex-fade overflow-y-auto overflow-x-hidden"
        style={{
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorY: "auto",
          // Reserve space at the END of the scroll area so content
          // doesn't get hidden behind the floating BottomNav. We do it
          // HERE instead of via BottomNav's spacer because the spacer
          // sits OUTSIDE main in the new flex layout and would leave
          // an empty white strip at the viewport's bottom.
          paddingBottom: "calc(env(safe-area-inset-bottom, 12px) + 96px)",
        }}
      >
        {children}

        <footer className="mt-24 border-t border-[#EAEAEA] hidden lg:block">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <LexiqLogo size={36} />
                <LexiqWordmark className="text-2xl" testid="footer-lexiq" />
              </div>
              <p className="text-sm text-[#4A4A4A] leading-relaxed">
                مساعد قانوني ذكي مكرَّس للنظام القانوني العراقي. تحليل قضايا، صياغة وثائق،
                وبحث قانوني — بمنهجية محامٍ كبير.
              </p>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#8A8A8A] mb-3">روابط</div>
              <ul className="space-y-2 text-sm text-[#4A4A4A]">
                <li><NavLink to="/analyze" className="editorial-link" data-testid="footer-analyze">تحليل قضية</NavLink></li>
                <li><NavLink to="/draft" className="editorial-link" data-testid="footer-draft">صياغة وثيقة</NavLink></li>
                <li><NavLink to="/chat" className="editorial-link" data-testid="footer-chat">البحث القانوني</NavLink></li>
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#8A8A8A] mb-3">تنبيه قانوني</div>
              <p className="text-sm text-[#4A4A4A] leading-relaxed">
                المخرجات إرشادية ولا تشكّل استشارة قانونية رسمية. يُرجى مراجعة محامٍ مرخّص
                قبل اتخاذ أي إجراء قضائي.
              </p>
            </div>
          </div>
          <div className="gold-divider" />
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-5 text-xs text-[#8A8A8A] flex justify-between">
            <span>© {new Date().getFullYear()} <LexiqWordmark className="text-[12px]" testid="copyright-lexiq" />. جميع الحقوق محفوظة.</span>
            <span className="font-amiri">جمهورية العراق · القانون العراقي</span>
          </div>
        </footer>
      </main>
      <BottomNav />
    </div>
  );
}
